import React, { useEffect, useRef, useState } from 'react';
import PopularDefenseDesktop from './Desktop/PopularDefense';
import PopularDefenseMobile from './Mobile/PopularDefense';
import AttackStats from '../components/AttackStats';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * **통계 탭** — 공덱 · 방덱 · 길드원 카드를 눌러 **그 안으로 들어간다.**
 *
 * 전투기록 탭과 같은 걸음걸이다(시즌 카드 → 그 안 → [← 뒤로가기]). 예전에는 고르는 줄이
 * 늘 위에 남고 아래 내용만 바뀌었는데, 같은 사이트에서 탭마다 걸음이 다르면 어디에 있는지
 * 헷갈린다. 들어가면 카드는 접히고 맨 위에 뒤로가기만 남는다.
 *
 * 카드 차림은 전투기록의 시즌 카드(`Common/SeasonSelect`)를 그대로 따랐다 — 작은 영문
 * 이름표 위, 큰 그라데이션 글씨 아래.
 *
 * 방덱은 예전부터 있던 화면(`PopularDefense`)을 그대로 쓴다. 그 화면은 데스크톱·모바일이
 * 따로라 여기서 갈라 준다. 공덱과 길드원은 목록 모양이 같아 한 벌(`AttackStats`)로 두고
 * 어느 쪽인지만 넘긴다.
 */

type View = 'atk' | 'def' | 'member';

const CARDS: Array<{ key: View; tag: string; label: string }> = [
    { key: 'atk', tag: 'ATTACK', label: '공덱' },
    { key: 'def', tag: 'DEFENSE', label: '방덱' },
    { key: 'member', tag: 'MEMBER', label: '길드원' }
];

const PAGE: React.CSSProperties = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: '100vh'
};

interface Props {
    monsterMapping: Record<string, any>;
    user: any;
    isMobile: boolean;
}

const StatsTab: React.FC<Props> = ({ monsterMapping, user, isMobile }) => {
    /** null 이면 고르는 화면 */
    const [view, setView] = useState<View | null>(null);
    /** 프로필 창의 [길드원 통계] 로 들어왔을 때 처음 고를 사람 — 바뀌면 길드원 화면을 새로 그린다 */
    const [myName, setMyName] = useState<{ name: string; at: number } | null>(null);
    useEffect(() => {
        const on = (e: Event) => {
            const name = (e as CustomEvent).detail?.nickname;
            if (!name) return;
            setMyName({ name, at: Date.now() });
            setView('member');
        };
        window.addEventListener('swpvpdb:open-my-stats', on);
        return () => window.removeEventListener('swpvpdb:open-my-stats', on);
    }, []);

    /**
     * **좁은 화면인지는 잰 폭으로 정한다.**
     *
     * 폰인데도 데스크톱 판으로 그려지는 기기가 있다(사용자 화면 — 방덱 줄이 판을 넘어 잘렸다).
     * 데스크톱용 방덱 화면은 1200px 를 전제로 만들어져서 그런 기기에서는 쓸 수 없다.
     * 넘겨받은 `isMobile` 은 아직 재기 전(첫 그림)에만 쓴다.
     */
    const rootRef = useRef<HTMLDivElement>(null);
    const [w, setW] = useState(0);
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const read = () => setW(el.clientWidth);
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, [view]);
    const sm = w > 0 ? w < 560 : isMobile;

    // 뒤로 가기: 들어간 화면 → 고르는 화면
    useBackHandler(view !== null, () => setView(null));

    const idle = '0 10px 40px rgba(102, 126, 234, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)';
    const hover = '0 20px 60px rgba(102, 126, 234, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)';

    // ── 고르는 화면 ──
    if (view === null) {
        return (
            <div ref={rootRef} style={{ ...PAGE, padding: sm ? 12 : 24 }}>
                {/*
                  전투기록의 시즌 카드와 같은 움직임(`pulse-glow`). 셋이 한꺼번에 깜박이면
                  신호등 같아서, 카드마다 시작을 조금씩 늦춰 물결처럼 흐르게 한다.
                */}
                <style>
                    {`
            @keyframes stats-glow {
              0%, 100% { box-shadow: ${idle}, 0 0 0 0 rgba(102, 126, 234, 0); }
              50%      { box-shadow: ${idle}, 0 0 20px 4px rgba(102, 126, 234, 0.3); }
            }
          `}
                </style>
                <div style={{
                    width: '100%', maxWidth: 1200, margin: '0 auto',
                    padding: sm ? '16px 4px' : '40px 20px'
                }}>
                    {/*
                      전투기록의 시즌 화면과 **같은 짜임**이다 — 제목만 담은 판 하나, 그 아래
                      카드만 담은 판 하나. 한 판에 다 넣으면 같은 사이트에서 탭마다 다르게 보인다.
                    */}
                    <div style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 24,
                        padding: sm ? '24px 16px' : '40px 32px 32px',
                        marginBottom: sm ? 16 : 32,
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                        boxSizing: 'border-box',
                        textAlign: 'center'
                    }}>
                        <h2 style={{
                            margin: '0 0 8px', fontWeight: 900, fontSize: sm ? 26 : 40, letterSpacing: -1,
                            background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                        }}>통계</h2>
                    </div>

                    <div style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 24,
                        padding: sm ? 16 : 32,
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '100%',
                        minHeight: sm ? 200 : 400,
                        boxSizing: 'border-box'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 'clamp(12px, 3vw, 20px)',
                            width: '100%'
                        }}>
                            {CARDS.map(({ key, tag, label }, i) => (
                                <div
                                    key={key}
                                    onClick={() => setView(key)}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.6)',
                                        borderRadius: 'clamp(12px, 2.5vw, 16px)',
                                        padding: 'clamp(20px, 4vw, 28px) clamp(10px, 3vw, 24px)',
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: idle,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 'clamp(110px, 22vw, 140px)',
                                        position: 'relative',
                                        overflow: 'visible',
                                        border: '2px solid rgba(102, 126, 234, 0.3)',
                                        backdropFilter: 'blur(10px)',
                                        animation: `stats-glow 2.2s ease-in-out ${i * 0.5}s infinite`
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                                        e.currentTarget.style.boxShadow = hover;
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
                                        e.currentTarget.style.animation = 'none';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                        e.currentTarget.style.boxShadow = idle;
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)';
                                        e.currentTarget.style.animation = `stats-glow 2.2s ease-in-out ${i * 0.5}s infinite`;
                                    }}
                                >

                                    <div style={{
                                        fontSize: 'clamp(10px, 2vw, 12px)', fontWeight: 700, letterSpacing: '2px',
                                        color: '#667eea', marginBottom: 'clamp(5px, 1vw, 6px)', opacity: 0.7,
                                        position: 'relative', zIndex: 1, whiteSpace: 'nowrap'
                                    }}>{tag}</div>

                                    <div style={{
                                        fontSize: 'clamp(22px, 5vw, 40px)', fontWeight: 900,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        /*
                                          줄높이를 1 로 두면 **한글 아래가 잘린다** — 글자를 배경으로
                                          칠하는 방식(background-clip:text)이라 글상자 밖은 안 그려지는데,
                                          1 은 글꼴이 실제로 쓰는 높이보다 낮다. 시즌 카드는 숫자(22)만
                                          담아서 드러나지 않았다.
                                        */
                                        lineHeight: 1.3,
                                        position: 'relative', zIndex: 1, whiteSpace: 'nowrap'
                                    }}>{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── 들어간 화면 ──
    return (
        <div ref={rootRef} style={PAGE}>
            {view === 'def' ? (
                sm
                    ? <PopularDefenseMobile monsterMapping={monsterMapping} user={user} onBack={() => setView(null)} />
                    : <PopularDefenseDesktop monsterMapping={monsterMapping} user={user} onBack={() => setView(null)} />
            ) : (
                <AttackStats
                    key={view === 'member' && myName ? `me-${myName.at}` : String(view)}
                    monsterMapping={monsterMapping}
                    isMobile={sm}
                    mode={view === 'member' ? 'member' : 'guild'}
                    onBack={() => setView(null)}
                    initialName={view === 'member' ? myName?.name : undefined}
                />
            )}
        </div>
    );
};

export default StatsTab;
